import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FiBell, 
  FiCheck, 
  FiCheckCircle, 
  FiTrash2, 
  FiFilter, 
  FiPackage,
  FiCalendar,
  FiUser,
  FiAlertCircle
} from 'react-icons/fi';
import { useNotifications } from '../hooks/useNotifications';
import { toast } from 'react-toastify';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all'); // all, unread, read
  const { 
    notifications, 
    loading, 
    markAsRead, 
    markAllAsRead, 
    refetch 
  } = useNotifications({
    pollInterval: 30000
  });

  const handleNotificationClick = async (notification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      await markAsRead(notification.id);
    }

    // Navigate based on resource_type
    switch (notification.resource_type) {
      case 'task':
        if (notification.resource_id) {
          navigate(`/tasks/${notification.resource_id}`);
        }
        break;
      case 'product_low_stock':
        if (notification.resource_id) {
          navigate(`/products/${notification.resource_id}`);
        }
        break;
      default:
        break;
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllAsRead();
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark notifications as read');
    }
  };

  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'unread') return !notification.is_read;
    if (filter === 'read') return notification.is_read;
    return true;
  });

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'product_low_stock':
        return FiPackage;
      case 'task_assigned':
      case 'task_reassigned':
        return FiUser;
      case 'task_status_changed':
      case 'task_completed':
        return FiCheckCircle;
      case 'task_due_soon':
      case 'task_overdue':
        return FiCalendar;
      default:
        return FiBell;
    }
  };

  const getNotificationColor = (type) => {
    const colors = {
      task_assigned: 'text-blue-600 bg-blue-50 border-blue-100',
      task_reassigned: 'text-purple-600 bg-purple-50 border-purple-100',
      task_status_changed: 'text-yellow-600 bg-yellow-50 border-yellow-100',
      task_due_soon: 'text-orange-600 bg-orange-50 border-orange-100',
      task_overdue: 'text-red-600 bg-red-50 border-red-100',
      task_completed: 'text-green-600 bg-green-50 border-green-100',
      product_low_stock: 'text-red-600 bg-red-50 border-red-100',
    };
    return colors[type] || 'text-gray-600 bg-gray-50 border-gray-100';
  };

  const formatTime = (datetime) => {
    if (!datetime) return '';
    const d = new Date(datetime);
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit'
    });
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-5xl">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FiBell className="text-accent-gold" />
            Notifications
          </h1>
          <p className="text-gray-500 mt-1">
            Stay updated with your tasks and system alerts
          </p>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex bg-white rounded-lg shadow-sm border border-gray-200 p-1">
            <button
              onClick={() => setFilter('all')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === 'all' 
                  ? 'bg-gray-100 text-gray-900' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setFilter('unread')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                filter === 'unread' 
                  ? 'bg-blue-50 text-blue-700' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Unread
            </button>
          </div>

          <button
            onClick={handleMarkAllRead}
            className="flex items-center gap-2 px-4 py-2 bg-white text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors shadow-sm"
          >
            <FiCheckCircle className="w-4 h-4" />
            <span className="hidden sm:inline">Mark all read</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading && notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-gold mb-4"></div>
            <p className="text-gray-500">Loading notifications...</p>
          </div>
        ) : filteredNotifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
              <FiBell className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">
              No notifications found
            </h3>
            <p className="text-gray-500 max-w-sm mx-auto">
              {filter === 'unread' 
                ? "You're all caught up! No unread notifications." 
                : "You don't have any notifications yet."}
            </p>
            {filter !== 'all' && (
              <button
                onClick={() => setFilter('all')}
                className="mt-4 text-accent-gold hover:text-accent-goldDark font-medium"
              >
                View all notifications
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {filteredNotifications.map((notification) => {
              const Icon = getNotificationIcon(notification.notification_type);
              const colorClass = getNotificationColor(notification.notification_type);
              
              return (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 sm:p-6 hover:bg-gray-50 cursor-pointer transition-colors group ${
                    !notification.is_read ? 'bg-blue-50/30' : ''
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center border ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0 pt-1">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <p className={`text-base ${!notification.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                            {notification.message}
                          </p>
                          <p className="text-sm text-gray-500 mt-1 flex items-center gap-2">
                            <span>{formatTime(notification.created_at)}</span>
                            <span className="text-gray-300">•</span>
                            <span className="capitalize text-gray-500">
                              {notification.notification_type.replace(/_/g, ' ')}
                            </span>
                          </p>
                        </div>
                        
                        {!notification.is_read && (
                          <span className="flex-shrink-0 w-2.5 h-2.5 bg-blue-600 rounded-full mt-2"></span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
